import { useTranslation } from 'react-i18next';
import { AgentTools, type ToolCall, type ToolCallSummary } from '@/features/types/tools';
import { ToolCallContainer } from '../../ToolCallContainer';
import { ToolCallContentBox } from '../../ToolCallContentBox';
import { ToolCallDataList } from '../../ToolCallDataList';
import { useToolCallParser, ToolCallState } from '../../useToolCallParser';
import { ToolCallError } from '../../ToolCallError';

interface GetSObjectTool {
  name: AgentTools.sf_get_sobject;
  args: { objectName: string; recordId: string; fields?: string[] };
}

export type GetSObjectToolCall = ToolCall<GetSObjectTool['name'], GetSObjectTool['args']>;

export type GetSObjectToolCallSummary = ToolCallSummary<
  GetSObjectTool['name'],
  GetSObjectTool['args']
>;

interface GetSObjectResult {
  [key: string]: unknown;
}

export function GetSObject({ toolCall }: { toolCall: GetSObjectToolCallSummary }) {
  const { t } = useTranslation('prince');

  const { parsedResult, title } = useToolCallParser<GetSObjectResult>(
    toolCall,
    ({ toolCallState }) => {
      const recordId = toolCall.args.recordId;
      switch (toolCallState) {
        case ToolCallState.Running:
          return t('toolCalls.sf_get_sobject.title.whileRunning', { id: recordId });
        case ToolCallState.Error:
          return t('toolCalls.sf_get_sobject.title.errors', { id: recordId });
        case ToolCallState.Success:
          return t('toolCalls.sf_get_sobject.title.success', { id: recordId });
      }
    }
  );

  const tabs = [
    {
      id: 'results',
      label: t('toolCalls.results'),
      showLoadingWhileRunning: true,
      content:
        !toolCall.isRunning && parsedResult ? (
          parsedResult.hasErrors ? (
            <ToolCallError parsedResult={parsedResult} />
          ) : (
            <ToolCallContentBox content={toolCall.results} />
          )
        ) : null,
    },
    {
      id: 'parameters',
      label: t('toolCalls.parameters'),
      content: (
        <ToolCallDataList
          items={[
            {
              label: t('toolCalls.sf_get_sobject.parameters.objectName'),
              value: toolCall.args.objectName,
            },
            {
              label: t('toolCalls.sf_get_sobject.parameters.recordId'),
              value: toolCall.args.recordId,
            },
            {
              label: t('toolCalls.sf_get_sobject.parameters.fields'),
              value: toolCall.args.fields?.join(', ') || '',
            },
          ]}
        />
      ),
    },
  ];

  return (
    <ToolCallContainer
      toolName={toolCall.name}
      title={title}
      isRunning={toolCall.isRunning}
      tabs={tabs}
    />
  );
}
